import _ from 'lodash';
import moment from 'moment/moment';
import { getConfig } from './configUtil';
import { PATIENT_CONTEXT_TABS_ID } from '../pages/ward/forms/formsUtil';

export const BASE_TAB = PATIENT_CONTEXT_TABS_ID.PATIENT;

export const INIT_TIMESTAMP_PATIENT = {
    patient: null,
    timestamp: null,
};

export const INIT_PREV_OBS_STATS = {
    totalScore: null,
    riskScore: null,
    obsDate: null,
};

export const PATIENT_CONTEXT_TYPE = {
    ED: 'ED',
    EDAU: 'EDAU',
    SDEC: 'SDEC',
    UTC: 'UTC',
    INPATIENT: 'INPATIENT',
    OUTPATIENT: 'OUTPATIENT',
    REVIEW: 'REVIEW',
    PROCEDURAL: 'PROCEDURAL',
};

export const PATIENT_AGE_TYPE = {
    ADULT: 1,
    PAEDS: 2,
    OVER75: 3,
};

export const getStringAgeType = (ageType) => {
    switch (ageType) {
        case PATIENT_AGE_TYPE.ADULT:
            return 'adult';
        case PATIENT_AGE_TYPE.PAEDS:
            return 'paeds';
        case PATIENT_AGE_TYPE.OVER75:
            return 'over75';
        default:
            return 'unknown';
    }
};

export const DISCHARGE_MODAL_TYPE_TRANSFER = {
    SELECT: 'SELECT',
    DISCHARGE: 'DISCHARGE',
    TRANSFER_CLINICIAN_SPEC: 'TRANSFER_CLINICIAN_SPEC',
    TRANSFER: 'TRANSFER',
    TRANSFER_ED: 'TRANSFER_ED',
    IP_DISCHARGE: 'IP_DISCHARGE',
    ADMISSION: 'ADMISSION',
    OLD_EDAU: 'OLD_EDAU',
};

export const ASSESSMENT_MANDATORY_CONFIG = {
    // MainScreen button red borderline
    ALWAYS_SHOW_BORDERLINE: 'alwaysShowBorderline',
    SHOW_BORDERLINE_ON_DISCHARGE: 'showBorderlineOnDischarge',
    DONT_SHOW_BORDERLINE: 'dontShowBorderline',

    // Additional modal shown on discharge
    DISPLAY_MODAL_ON_DISCHARGE: 'displayModalOnDischarge',
    DONT_DISPLAY_MODAL_ON_DISCHARGE: 'dontDisplayModalOnDischarge',
};

const PRESENTATION_ILLNESS = ['Mental illness', 'Overdose and poisoning', 'Self-harm', 'Behaving strangely'];

export const isOver65Patient = (dob) => {
    return moment().diff(dob, 'years') > 65;
};

export const getSafeguardingLevel = (currentPatientInWard) => {
    let level = null;
    if (currentPatientInWard?.studies?.paedSafeguarding) {
        _.forEach(currentPatientInWard.studies.paedSafeguarding.list, (item) => {
            if (!item.canceled && item.safeguarding.level && !level) level = item.safeguarding.level;
        });
    }
    return level;
};

export const isMentalHealthFormNeeded = (triage) => {
    let isNeeded = false;
    if (
        getConfig('mentalHealthFormDialog', false) &&
        triage?.discriminator?.presentation &&
        PRESENTATION_ILLNESS.includes(triage.discriminator.presentation)
    ) {
        isNeeded = true;
    }
    return isNeeded;
};

export const isMentalHealthFormComplete = (currentPatientInWard) => {
    return getConfig('mentalHealthFormDialog', false) && currentPatientInWard?.studies?.mentalHealthForm;
};

export const checkCauseForConcernForm = (currentPatient) => {
    return (
        getConfig('causeForConcernMandatory', false) &&
        !isPaedsPatient(currentPatient?.patient?.dob) &&
        currentPatient?.triage?.safeguardingConcerns &&
        !currentPatient.studies?.causeForConcernForm
    );
};

export const isCauseForConcernFormComplete = (currentPatientInWard) => {
    return currentPatientInWard?.studies?.causeForConcernForm;
};

export const isPaedsPatient = (dob) => {
    return moment().diff(dob, 'years') < 18;
};
export const checkSafeguardingLevelForm = (currentPatient) => {
    return (
        getConfig('safeguardingLevelMandatory', false) &&
        currentPatient?.patient?.dob &&
        isPaedsPatient(currentPatient?.patient?.dob) &&
        !getSafeguardingLevel(currentPatient) &&
        currentPatient?.studies?.referrals
    );
};

export const checkCFSForm = (currentPatient) => {
    return (
        getConfig('CFSMandatory', false) &&
        isOver65Patient(currentPatient?.patient?.dob) &&
        !haveClinicalFrailtyScore(currentPatient) &&
        !currentPatient?.metadata?.CFSNotRequired?.acknowledge
    );
};

export const haveClinicalFrailtyScore = (currentPatientInWard) => {
    let haveCFS = false;
    if (currentPatientInWard?.studies?.rockwood) {
        _.forEach(currentPatientInWard.studies.rockwood.list, (item) => {
            if (!item.canceled && !haveCFS) haveCFS = true;
        });
    }
    return haveCFS;
};

export const getSafeguardingLevelFormValue = (paedSafeguarding) => {
    let level = null;
    if (paedSafeguarding) {
        _.forEach(paedSafeguarding.list, (item) => {
            if (!item.canceled && item.safeguarding.level && !level) level = item.safeguarding.level;
        });
    }
    return level;
};

export const selectSafeLevelColor = (safeguardingLevel) => {
    // eslint-disable-next-line default-case
    switch (safeguardingLevel) {
        case 'green':
            return '#467055';
        case 'amber':
            return '#F7A202';
        case 'red':
            return '#E25B61';
    }
};

export const getTabHeight = (id) => {
    let height = document.getElementById('patientInfoRow.' + id)?.offsetHeight || 55;
    let informationBarHeight = document.getElementById('information-bar')?.offsetHeight
        ? document.getElementById('information-bar').offsetHeight - 20
        : 0;
    height += informationBarHeight;
    if (getConfig('actionButtonsOnBottomBar', false)) {
        height += 110;
    } else {
        height += informationBarHeight > 0 ? 0 : 40;
    }
    return { height };
};

export const cleanObject = (obj) => {
    for (let propName in obj) {
        if (obj[propName]?.length === 0 || obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
            delete obj[propName];
        }
    }
    return obj;
};

export const getFormCustomTitle = (form, patientType, patientAgeType) => {
    if (form?.options?.dynamicTitle) {
        switch (form.id) {
            case 'msgToGp':
                return patientType !== PATIENT_CONTEXT_TYPE.EDAU ? 'Message to GP / Plan & Advice Given' : 'Message to GP';
            case 'injury':
                return patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? 'Injury' : 'CWILTED';
            case 'nutritionScreen':
                return patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? 'Weight / Height / BMI' : 'Weight / Height';
            case 'clerkingED':
                return `${patientType} Clerking`;
            default:
                return form?.formTitle;
        }
    }
    return form?.formTitle || 'UNK';
};

export const isPatientED = (patientType) => {
    return patientType === PATIENT_CONTEXT_TYPE.ED || patientType === PATIENT_CONTEXT_TYPE.UTC || patientType === PATIENT_CONTEXT_TYPE.SDEC;
};

export const getPatientEDContextType = (wardId) => {
    if (_.startsWith(wardId, 'ED_')) return PATIENT_CONTEXT_TYPE.ED;
    if (_.startsWith(wardId, 'SDEC_')) return PATIENT_CONTEXT_TYPE.SDEC;
    if (_.startsWith(wardId, 'UTC_')) return PATIENT_CONTEXT_TYPE.UTC;
    if (_.startsWith(wardId, '0EDAU')) return PATIENT_CONTEXT_TYPE.EDAU;
    return PATIENT_CONTEXT_TYPE.ED;
};
